import Vue from 'vue';
import Router from 'vue-router';
import { getSystemConfig } from '@/utils/systemConfig';
import LoginPanel from '../components/LoginPanel.vue';
import Layout from '../layout/Layout.vue';
import Dashboard from '../views/Dashboard.vue';
import OrgManage from '../views/OrgManage.vue';
import StaffManage from '../views/StaffManage.vue';
import StudentManage from '../views/StudentManage.vue';
import RoleManage from '../views/RoleManage.vue';
import DataStatistics from '../views/DataStatistics.vue';
import ElectronicMap from '../views/ElectronicMap.vue';
import SystemSettings from '../views/SystemSettings.vue';
import ROI from '../views/ROI.vue';
import ROIOverview from '../views/ROIOverview.vue';
import ROIAudit from '../views/ROIAudit.vue';
import BaiduAifanfan from '../views/BaiduAifanfan.vue';
import RealNameReview from '../views/RealNameReview.vue';
import WithdrawAudit from '../views/WithdrawAudit.vue';
import StaffOrders from '../views/StaffOrders.vue';
import { getToken, getAllowedMenus, clearSessionAuth } from '@/utils/sessionAuth';
import { getFirstAllowedPath, isRouteAllowed, isMenuKeyAllowed } from '@/utils/menuPermissions';
import { buildManageLoginEntryUrl } from '@/utils/manageEntryRedirect.mjs';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      redirect: '/login'
    },
    {
      path: '/login',
      name: 'Login',
      component: LoginPanel,
      meta: { title: '学途无忧后台管理系统' }
    },
    {
      path: '/',
      component: Layout,
      children: [
        {
          path: 'dashboard',
          name: 'Dashboard',
          component: Dashboard,
          meta: { title: '首页', menuKey: 'dashboard' }
        },
        {
          path: 'statistics',
          name: 'DataStatistics',
          component: DataStatistics,
          meta: { title: '数据统计', menuKey: 'statistics' }
        },
        {
          path: 'electronic-map',
          name: 'ElectronicMap',
          component: ElectronicMap,
          meta: { title: '电子地图', menuKey: 'electronic-map' }
        },
        {
          path: 'org',
          name: 'OrgManage',
          component: OrgManage,
          meta: { title: '机构管理', menuKey: 'org' }
        },
        {
          path: 'staff',
          name: 'StaffManage',
          component: StaffManage,
          meta: { title: '会员管理', menuKey: 'staff' }
        },
        {
          path: 'staff-orders',
          name: 'StaffOrders',
          component: StaffOrders,
          meta: { title: '会员订单', menuKey: 'staff' }
        },
        {
          path: 'student',
          name: 'StudentManage',
          component: StudentManage,
          meta: { title: '学生管理', menuKey: 'student' }
        },
        {
          path: 'baidu-aifanfan',
          name: 'BaiduAifanfan',
          component: BaiduAifanfan,
          meta: { title: '百度爱番番', menuKey: 'baidu-aifanfan' }
        },
        {
          path: 'roi',
          name: 'ROI',
          component: ROI,
          meta: { title: '推广账户管理', menuKey: 'roi:promotion' }
        },
        {
          path: 'roi-overview',
          name: 'ROIOverview',
          component: ROIOverview,
          meta: { title: '推广账户概况', menuKey: 'roi:overview' }
        },
        {
          path: 'roi-audit',
          name: 'ROIAudit',
          component: ROIAudit,
          meta: { title: 'ROI监控', menuKey: 'roi:audit' }
        },
        {
          path: 'real-name-review',
          name: 'RealNameReview',
          component: RealNameReview,
          meta: { title: '实名审核' }
        },
        {
          path: 'withdraw-audit',
          name: 'WithdrawAudit',
          component: WithdrawAudit,
          meta: { title: '提现审核' }
        },
        {
          path: 'role',
          name: 'RoleManage',
          component: RoleManage,
          meta: { title: '角色管理', menuKey: 'role' }
        },
        {
          path: 'dict',
          name: 'DictManage',
          redirect: (to) => ({
            path: '/system-settings',
            query: { ...to.query, tab: 'dictManage' }
          }),
          meta: { title: '字典管理', menuKey: 'dict' }
        },
        {
          path: 'log',
          name: 'LogManage',
          redirect: (to) => ({
            path: '/system-settings',
            query: { ...to.query, tab: 'logManage' }
          }),
          meta: { title: '日志管理', menuKey: 'log' }
        },
        {
          path: 'system-settings',
          name: 'SystemSettings',
          component: SystemSettings,
          meta: { title: '系统设置', menuKey: 'system-settings' }
        }
      ]
    }
  ]
});

router.beforeEach((to, from, next) => {
  if (to.path === '/login') {
    next();
    return;
  }
  const token = getToken();
  if (!token) {
    if (to.path === '/baidu-aifanfan' && to.query && (to.query.keyword || to.query.tab === 'review')) {
      const params = new URLSearchParams();
      params.set('tab', 'review');
      if (to.query.keyword) params.set('keyword', String(to.query.keyword));
      window.location.href = buildManageLoginEntryUrl(`/baidu-aifanfan?${params.toString()}`);
      return;
    }
    next('/login');
    return;
  }
  const menus = getAllowedMenus();
  if (!menus || !menus.length) {
    sessionStorage.setItem('loginFlashMessage', '登录已失效或未配置菜单权限，请重新登录');
    clearSessionAuth();
    localStorage.removeItem('token');
    localStorage.removeItem('account');
    localStorage.removeItem('userRole');
    localStorage.removeItem('roleCode');
    localStorage.removeItem('allowedMenus');
    next('/login');
    return;
  }
  const last = to.matched.length ? to.matched[to.matched.length - 1] : null;
  const menuKey = last && last.meta ? last.meta.menuKey : null;
  let allowed = !menuKey || isRouteAllowed(menuKey, menus);
  if (menuKey === 'system-settings' && !allowed && to.query) {
    const t = String(to.query.tab || '');
    if (t === 'dictManage' && isMenuKeyAllowed('dict', menus)) {
      allowed = true;
    } else if (t === 'logManage' && isMenuKeyAllowed('log', menus)) {
      allowed = true;
    }
  }
  if (menuKey && !allowed) {
    const fallback = getFirstAllowedPath(menus);
    if (!fallback) {
      sessionStorage.setItem('loginFlashMessage', '当前账号无可用菜单权限');
      clearSessionAuth();
      localStorage.removeItem('token');
      localStorage.removeItem('account');
      localStorage.removeItem('userRole');
      localStorage.removeItem('roleCode');
      localStorage.removeItem('allowedMenus');
      next('/login');
      return;
    }
    next({ path: fallback, replace: true });
    return;
  }
  next();
});

router.afterEach((to) => {
  const title = to.meta && to.meta.title;
  const cfg = getSystemConfig();
  const systemTitle = cfg.systemTitle || '学途无忧后台管理系统';
  if (to.path === '/login') {
    document.title = systemTitle;
  } else if (title) {
    document.title = title;
  } else {
    document.title = systemTitle;
  }
});

export default router;

